import { cn } from "@/lib/utils";

export interface CrmTaskFilterOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

interface CrmTaskStatusFilterTabsProps<TValue extends string> {
  options: readonly CrmTaskFilterOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
}

interface CrmTaskReviewFiltersPanelProps<TDueValue extends string> {
  activeFilterCount: number;
  onClearFilters: () => void;
  typeFilter: string;
  taskTypeOptions: readonly CrmTaskFilterOption[];
  onTypeFilterChange: (value: string) => void;
  assigneeFilter: string;
  assigneeOptions: readonly CrmTaskFilterOption[];
  unassignedAssigneeValue: string;
  onAssigneeFilterChange: (value: string) => void;
  sourceFilter: string;
  onSourceFilterChange: (value: string) => void;
  triggerFilter: string;
  onTriggerFilterChange: (value: string) => void;
  dueFilter: TDueValue;
  dueFilterOptions: readonly CrmTaskFilterOption<TDueValue>[];
  onDueFilterChange: (value: TDueValue) => void;
  scopedQueueLabel: string | null;
  onClearScope: () => void;
  isLoading: boolean;
  visibleCount: number;
  totalCount: number;
  hasNextPage: boolean;
}

export function CrmTaskStatusFilterTabs<TValue extends string>({
  options,
  value,
  onChange,
}: CrmTaskStatusFilterTabsProps<TValue>) {
  return (
    <div className="flex gap-1 rounded-lg border border-[var(--color-border)] p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            value === option.value
              ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
              : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function CrmTaskReviewFiltersPanel<TDueValue extends string>({
  activeFilterCount,
  onClearFilters,
  typeFilter,
  taskTypeOptions,
  onTypeFilterChange,
  assigneeFilter,
  assigneeOptions,
  unassignedAssigneeValue,
  onAssigneeFilterChange,
  sourceFilter,
  onSourceFilterChange,
  triggerFilter,
  onTriggerFilterChange,
  dueFilter,
  dueFilterOptions,
  onDueFilterChange,
  scopedQueueLabel,
  onClearScope,
  isLoading,
  visibleCount,
  totalCount,
  hasNextPage,
}: CrmTaskReviewFiltersPanelProps<TDueValue>) {
  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Review filters
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Slice the queue by reusable task type, assignee, automation source, trigger, and due window.
          </p>
        </div>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={onClearFilters}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
          >
            Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
          </button>
        )}
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Task type
          </span>
          <select
            value={typeFilter}
            onChange={(event) => onTypeFilterChange(event.target.value)}
            aria-label="Filter tasks by type"
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="all">All task types</option>
            {taskTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Assignee
          </span>
          <select
            value={assigneeFilter}
            onChange={(event) => onAssigneeFilterChange(event.target.value)}
            aria-label="Filter tasks by assignee"
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="all">All assignees</option>
            <option value={unassignedAssigneeValue}>Unassigned</option>
            {assigneeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <CrmTaskReviewTextFilter
          label="Source"
          value={sourceFilter}
          placeholder="automation, manual, system"
          ariaLabel="Filter tasks by source"
          onChange={onSourceFilterChange}
        />
        <CrmTaskReviewTextFilter
          label="Trigger"
          value={triggerFilter}
          placeholder="email.replied, booking.created"
          ariaLabel="Filter tasks by trigger"
          onChange={onTriggerFilterChange}
        />
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Due window
          </span>
          <select
            value={dueFilter}
            onChange={(event) => onDueFilterChange(event.target.value as TDueValue)}
            aria-label="Filter tasks by due window"
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {dueFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {scopedQueueLabel && (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-[var(--color-primary)]/25 bg-[var(--color-primary-light)]/50 px-3 py-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)]">Scoped queue</p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Showing tasks linked to {scopedQueueLabel}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClearScope}
            className="w-fit rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Clear record scope
          </button>
        </div>
      )}
      {!isLoading && (
        <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
          Showing {visibleCount} of {totalCount} matching task{totalCount === 1 ? "" : "s"}.
          {hasNextPage ? " More tasks match these filters; narrow the queue further to review them." : ""}
        </p>
      )}
    </section>
  );
}

function CrmTaskReviewTextFilter({
  label,
  value,
  placeholder,
  ariaLabel,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  ariaLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
      />
    </label>
  );
}
